/**
 * PermissionGate — 声明式权限点 children guard。
 *
 * 用法：
 *   <PermissionGate point={PERMISSIONS.REFUND_APPROVE}>
 *     <Button onClick={approve}>批准退款</Button>
 *   </PermissionGate>
 *
 *   <PermissionGate point={PERMISSIONS.PII_READ_L5} fallback={<Tag>需法务申请</Tag>}>
 *     <PhoneNumber value={user.phone} />
 *   </PermissionGate>
 *
 * 行为：
 *   - usePermission(point)=true → 渲染 children
 *   - usePermission(point)=false 且 fallback 提供 → 渲染 fallback
 *   - usePermission(point)=false 且无 fallback → 渲染 null（不在 DOM 中）
 *
 * 注意：UI 隐藏不是安全边界 —— 真正校验在 admin-service requirePermission middleware。
 * PermissionGate 仅提供更好的用户体验（避免按下后弹 403）。
 *
 * 跨 stream 接口契约 §6.4 / §6.5。
 */
import type { ReactNode } from 'react';
import { usePermission } from '../hooks/usePermission';
import type { Permission } from '../contracts/permissions';

export interface PermissionGateProps {
  point: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}

export function PermissionGate({ point, children, fallback }: PermissionGateProps) {
  const allowed = usePermission(point);
  if (allowed) return <>{children}</>;
  return fallback !== undefined ? <>{fallback}</> : null;
}
